import { estimateScaleDenominator } from './scaleFromZoom';
import type {
  MapFrameCamera,
  ScaleBarDistanceUnit,
  ScaleBarElement,
  ScaleBarFittingStrategy,
  ScaleBarUnitSettings,
  ScaleBarUnits,
} from './layoutTypes';

export interface ScaleBarGeometry {
  readonly widthMm: number;
  readonly groundMeters: number;
  readonly label: string;
  readonly divisions: number;
  readonly subdivisions: number;
  readonly divisionValue: number;
  readonly unit: ScaleBarDistanceUnit;
  readonly unitLabel: string;
  readonly divisionLabels: readonly string[];
}

export interface ScaleBarElementGeometry {
  readonly upper: ScaleBarGeometry;
  readonly lower: ScaleBarGeometry | null;
}

/** Leaves room after the bar for the spelled-out unit caption. */
export const SCALE_BAR_CAPTION_ROOM = 0.68;

const METERS_PER_UNIT: Readonly<Record<ScaleBarDistanceUnit, number>> = {
  meters: 1,
  kilometers: 1000,
  feet: 0.3048,
  miles: 1609.344,
};

const UNIT_LABEL: Readonly<Record<ScaleBarDistanceUnit, string>> = {
  meters: 'Meters',
  kilometers: 'Kilometers',
  feet: 'Feet',
  miles: 'Miles',
};

const UNIT_ABBREVIATION: Readonly<Record<ScaleBarDistanceUnit, string>> = {
  meters: 'm',
  kilometers: 'km',
  feet: 'ft',
  miles: 'mi',
};

const MIN_DIVISIONS = 1;
const MAX_AUTO_DIVISIONS = 12;

export function metersPerScaleBarUnit(unit: ScaleBarDistanceUnit): number {
  return METERS_PER_UNIT[unit];
}

export function scaleBarUnitLabel(unit: ScaleBarDistanceUnit): string {
  return UNIT_LABEL[unit];
}

function niceCandidates(maxValue: number): number[] {
  if (!(maxValue > 0)) return [1];
  const exponent = Math.floor(Math.log10(maxValue));
  const values: number[] = [];
  for (let power = exponent - 6; power <= exponent + 2; power += 1) {
    const magnitude = 10 ** power;
    for (const factor of [1, 2, 2.5, 5]) values.push(factor * magnitude);
  }
  return values.sort((a, b) => a - b);
}

function pickNice(maxValue: number): number {
  const candidates = niceCandidates(maxValue);
  let chosen = candidates[0] ?? 1;
  for (const candidate of candidates) {
    if (candidate <= maxValue) chosen = candidate;
    else break;
  }
  return chosen;
}

function formatTick(value: number): string {
  if (Number.isInteger(value)) return String(value);
  if (Math.abs(value) >= 100) return String(Number(value.toFixed(1)));
  return String(Number(value.toFixed(2)));
}

function validCount(value: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(MIN_DIVISIONS, Math.min(50, Math.round(value)));
}

function exactOrNice(
  value: number | null,
  targetPerDivision: number,
): number {
  return value != null && Number.isFinite(value) && value > 0
    ? value
    : pickNice(targetPerDivision);
}

/** Ground meters represented by paper width at the representative fraction. */
export function groundMetersForPaperMm(
  widthMm: number,
  scaleDenominator: number,
): number {
  if (!(scaleDenominator > 0) || !(widthMm > 0)) return 0;
  return (widthMm / 1000) * scaleDenominator;
}

export function paperMmForGroundMeters(
  groundMeters: number,
  scaleDenominator: number,
): number {
  if (!(scaleDenominator > 0) || !(groundMeters > 0)) return 0;
  return (groundMeters / scaleDenominator) * 1000;
}

/**
 * Applies one ArcGIS fitting strategy to one scale-bar unit row.
 * User-provided division values are exact; automatically selected values use
 * the existing cartographic 1/2/2.5/5 sequence.
 */
export function computeScaleBarUnitGeometry(
  scaleDenominator: number | null,
  targetWidthMm: number,
  settings: ScaleBarUnitSettings,
  fittingStrategy: ScaleBarFittingStrategy,
  fixedBarWidthMm = targetWidthMm,
): ScaleBarGeometry | null {
  if (
    scaleDenominator == null
    || !(scaleDenominator > 0)
    || !(targetWidthMm > 0)
  ) return null;

  const unitMeters = metersPerScaleBarUnit(settings.unit);
  const targetGroundUnits =
    groundMetersForPaperMm(targetWidthMm, scaleDenominator) / unitMeters;
  if (!(targetGroundUnits > 0)) return null;

  let divisions = validCount(settings.divisions, 4);
  let divisionValue = exactOrNice(
    settings.divisionValue,
    targetGroundUnits / divisions,
  );
  let widthMm = targetWidthMm;

  switch (fittingStrategy) {
    case 'adjustDivisions':
      divisions = Math.max(
        MIN_DIVISIONS,
        Math.min(
          MAX_AUTO_DIVISIONS,
          Math.floor(targetGroundUnits / divisionValue),
        ),
      );
      widthMm = paperMmForGroundMeters(
        divisionValue * divisions * unitMeters,
        scaleDenominator,
      );
      break;
    case 'adjustDivisionAndDivisions': {
      let bestDivisions = divisions;
      let bestValue = pickNice(targetGroundUnits / divisions);
      let bestFill = 0;
      for (let count = 2; count <= 8; count += 1) {
        const candidate = pickNice(targetGroundUnits / count);
        const fill = (candidate * count) / targetGroundUnits;
        if (fill <= 1 && fill > bestFill) {
          bestDivisions = count;
          bestValue = candidate;
          bestFill = fill;
        }
      }
      divisions = bestDivisions;
      divisionValue = bestValue;
      widthMm = paperMmForGroundMeters(
        divisionValue * divisions * unitMeters,
        scaleDenominator,
      );
      break;
    }
    case 'adjustWidth':
      widthMm = paperMmForGroundMeters(
        divisionValue * divisions * unitMeters,
        scaleDenominator,
      );
      break;
    case 'fixedBarWidth': {
      widthMm = Math.max(1, fixedBarWidthMm);
      const totalUnits =
        groundMetersForPaperMm(widthMm, scaleDenominator) / unitMeters;
      divisionValue = totalUnits / divisions;
      break;
    }
    case 'adjustDivision':
    default:
      divisionValue = pickNice(targetGroundUnits / divisions);
      widthMm = paperMmForGroundMeters(
        divisionValue * divisions * unitMeters,
        scaleDenominator,
      );
      break;
  }

  const totalUnits = divisionValue * divisions;
  const groundMeters = totalUnits * unitMeters;
  return {
    widthMm,
    groundMeters,
    label: `${formatTick(totalUnits)} ${UNIT_ABBREVIATION[settings.unit]}`,
    divisions,
    subdivisions: validCount(settings.subdivisions, 1),
    divisionValue,
    unit: settings.unit,
    unitLabel: scaleBarUnitLabel(settings.unit),
    divisionLabels: Array.from(
      { length: divisions + 1 },
      (_, index) => formatTick(divisionValue * index),
    ),
  };
}

function legacyUnit(
  scaleDenominator: number,
  maxWidthMm: number,
  units: Exclude<ScaleBarUnits, 'dual'>,
): ScaleBarDistanceUnit {
  const meters = groundMetersForPaperMm(maxWidthMm, scaleDenominator);
  if (units === 'imperial') return meters >= 1609.344 ? 'miles' : 'feet';
  return meters >= 1000 ? 'kilometers' : 'meters';
}

/** Backward-compatible helper used by existing single-bar callers and tests. */
export function computeScaleBarGeometry(
  scaleDenominator: number | null,
  maxWidthMm: number,
  units: ScaleBarUnits,
  divisions = 4,
): ScaleBarGeometry | null {
  if (scaleDenominator == null || units === 'dual') return null;
  return computeScaleBarUnitGeometry(
    scaleDenominator,
    maxWidthMm,
    {
      unit: legacyUnit(scaleDenominator, maxWidthMm, units),
      divisionValue: null,
      divisions,
      subdivisions: 1,
    },
    'adjustDivision',
  );
}

/** Legacy single-row layout with caption room already excluded. */
export function scaleBarLayout(
  scaleDenominator: number | null,
  rectWidthMm: number,
  units: ScaleBarUnits,
  divisions = 4,
): ScaleBarGeometry | null {
  return computeScaleBarGeometry(
    scaleDenominator,
    rectWidthMm * SCALE_BAR_CAPTION_ROOM,
    units,
    divisions,
  );
}

export function effectiveScaleBarDenominator(
  scaleBar: Pick<ScaleBarElement, 'computeAtCenter'>,
  camera: MapFrameCamera,
): number | null {
  if (scaleBar.computeAtCenter) return camera.scaleDenominator;
  return estimateScaleDenominator(camera.zoom, 0);
}

export function scaleBarElementLayout(
  scaleBar: ScaleBarElement,
  scaleDenominator: number | null,
): ScaleBarElementGeometry | null {
  const targetWidth = scaleBar.rect.width * SCALE_BAR_CAPTION_ROOM;
  const upper = computeScaleBarUnitGeometry(
    scaleDenominator,
    targetWidth,
    scaleBar.upper,
    scaleBar.fittingStrategy,
    scaleBar.fixedBarWidthMm,
  );
  if (!upper) return null;
  const lower = scaleBar.lower
    ? computeScaleBarUnitGeometry(
        scaleDenominator,
        targetWidth,
        scaleBar.lower,
        scaleBar.fittingStrategy === 'adjustDivisionAndDivisions'
          ? 'adjustDivision'
          : scaleBar.fittingStrategy === 'fixedBarWidth'
            ? 'adjustWidth'
            : scaleBar.fittingStrategy,
        scaleBar.fixedBarWidthMm,
      )
    : null;
  return { upper, lower };
}
